import React from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { Trans } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Dropdown } from 'common/components/antd/Dropdown/Dropdown';
import { ColumnsType } from 'common/components/antd/Table/Table';
import { Tag } from 'common/components/antd/Tag/Tag';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { formatDateFromISO } from 'common/utils/format.utils';
import {
  EReconciliationStatus,
  IReconciliationEntity,
  IReconciliationParticipant,
} from 'domains/reconciliation/types/reconciliation.types';
import { EReconColumnKeys, EReconParticipantsColumnKeys } from '../enums/statistic.enums';
import { SStatusCell } from '../statistic.page.style';

export const getColumns = (): ColumnsType<IReconciliationParticipant> => [
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.status.title" />,
    width: 130,
    key: EReconParticipantsColumnKeys.Status,
    dataIndex: EReconParticipantsColumnKeys.Status,
    render: (status: string) => (
      <SStatusCell>
        <Tag>
          <Trans i18nKey={`reconciliation.statistic.status.${status}`} />
        </Tag>
      </SStatusCell>
    ),
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.debtor.title" />,
    width: '30%',
    key: EReconParticipantsColumnKeys.Name,
    dataIndex: EReconParticipantsColumnKeys.Name,
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.totalIn.title" />,
    width: 110,
    key: EReconParticipantsColumnKeys.TotalIn,
    dataIndex: EReconParticipantsColumnKeys.TotalIn,
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.totalOut.title" />,
    width: 110,
    key: EReconParticipantsColumnKeys.TotalOut,
    dataIndex: EReconParticipantsColumnKeys.TotalOut,
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.net.title" />,
    width: 230,
    key: EReconParticipantsColumnKeys.Saldo,
    dataIndex: EReconParticipantsColumnKeys.Saldo,
  },
];

export const getDebtorColumns = (): ColumnsType<IReconciliationParticipant> => [
  ...IS_BANK_FE ?
    [{
      title: '',
      width: 0,
      key: EReconParticipantsColumnKeys.GUID,
      dataIndex: EReconParticipantsColumnKeys.GUID,
      render: () => null,
    }]
    : [],
  {
    title: IS_BANK_FE ? <Trans i18nKey="reconciliation.statistic.table.column.isBlocked.title" /> : '',
    width: 120,
    key: EReconParticipantsColumnKeys.IsBlocked,
    dataIndex: EReconParticipantsColumnKeys.IsBlocked,
    render: () => null,
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.creditor.title" />,
    width: '30%',
    key: EReconParticipantsColumnKeys.Name,
    dataIndex: EReconParticipantsColumnKeys.Name,
  },
  {
    title: IS_BANK_FE ? <Trans i18nKey="reconciliation.statistic.table.column.totalIn.title" /> : '',
    width: 110,
    key: EReconParticipantsColumnKeys.TotalIn,
    dataIndex: EReconParticipantsColumnKeys.TotalIn,
  },
  {
    title: IS_BANK_FE
      ? <Trans i18nKey="reconciliation.statistic.table.column.totalOut.title" />
      : <Trans i18nKey="reconciliation.statistic.table.column.amountReconciled.title" />,
    width: 110,
    key: EReconParticipantsColumnKeys.TotalOut,
    dataIndex: EReconParticipantsColumnKeys.TotalOut,
  },
  {
    title: IS_BANK_FE ? <Trans i18nKey="reconciliation.statistic.table.column.net.title" /> : '',
    width: 230,
    key: EReconParticipantsColumnKeys.Saldo,
    dataIndex: EReconParticipantsColumnKeys.Saldo,
  },
];

export const EReconRowAction = {
  Info: 'info',
} as const;

const reconActions = [
  {
    key: EReconRowAction.Info,
    label: <Trans i18nKey="reconciliation.statistic.table.column.action.dropdown.info" />,
  },
];

export const getReconsColumns = ({
  onDetailsClickHandler,
}: {
  onDetailsClickHandler: (record: IReconciliationEntity) => void;
}): ColumnsType<IReconciliationEntity> => [
  {
    title: '',
    width: 200,
    key: EReconParticipantsColumnKeys.IsBlocked,
    dataIndex: EReconParticipantsColumnKeys.IsBlocked,
    render: () => null,
  },
  {
    title: '',
    width: '30%',
    key: EReconParticipantsColumnKeys.Name,
    dataIndex: EReconParticipantsColumnKeys.GUID,
    render: () => null,
  },
  {
    title: '',
    width: 138,
    key: `${EReconColumnKeys.Calculations}.${EReconColumnKeys.TotalIn}`,
    dataIndex: [EReconColumnKeys.Calculations, EReconColumnKeys.TotalIn],
  },
  {
    title: '',
    width: 138,
    key: `${EReconColumnKeys.Calculations}.${EReconColumnKeys.TotalOut}`,
    dataIndex: [EReconColumnKeys.Calculations, EReconColumnKeys.TotalOut],
  },
  {
    title: '',
    width: 80,
    key: `${EReconColumnKeys.Calculations}.${EReconColumnKeys.Saldo}`,
    dataIndex: [EReconColumnKeys.Calculations, EReconColumnKeys.Saldo],
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.created.title" />,
    width: 122,
    key: EReconColumnKeys.EndDate,
    dataIndex: EReconColumnKeys.EndDate,
    render: (value: string) => formatDateFromISO(value),
  },
  {
    title: <Trans i18nKey="reconciliation.statistic.table.column.createdAt.title" />,
    width: 122,
    key: EReconColumnKeys.CreatedAt,
    dataIndex: EReconColumnKeys.CreatedAt,
    render: (value: string) => formatDateFromISO(value),
  },
  {
    title: '',
    width: 45,
    key: EReconColumnKeys.CreatedAt,
    render: (_: string, record: IReconciliationEntity) =>
      record.status === EReconciliationStatus.Calculated
        ? (
            <Button
              style={{ cursor: 'pointer' }}
              type="primary"
              onClick={(): void => {
                onDetailsClickHandler(record);
              }}
            >
              <Trans i18nKey="reconciliation.statistic.table.column.paid.button.title" />
            </Button>
          )
        : (
            <Dropdown
              menu={{
                items: reconActions,
                onClick: () => onDetailsClickHandler(record),
              }}
              placement="topRight"
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          ),
  },
];
